import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, ExternalLink, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [aiStatus, setAiStatus] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | with_story | missing_story
  const [busyId, setBusyId] = useState(null);
  const [sortKey, setSortKey] = useState("name"); // name | word_count | ai_story_at | country
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const loadStatus = async () => {
    try {
      const { data } = await http.get("/admin/organizations/ai-stories/status");
      setAiStatus(data);
    } catch {}
  };

  const loadOrgs = async () => {
    try {
      const { data } = await http.get("/organizations", { params: { limit: 500 } });
      setOrgs(data);
    } catch { toast.error("Failed to load organizations"); }
  };

  useEffect(() => { loadOrgs(); loadStatus(); }, []);

  // Poll AI status every 5s while a bulk regen is running.
  useEffect(() => {
    if (!regenerating) return;
    const iv = setInterval(() => { loadStatus(); loadOrgs(); }, 5000);
    return () => clearInterval(iv);
  }, [regenerating]);

  // Also stop the "regenerating" flag automatically once missing reaches 0.
  useEffect(() => {
    if (regenerating && aiStatus && aiStatus.missing === 0) {
      setRegenerating(false);
      toast.success("Backfill complete");
    }
  }, [aiStatus, regenerating]);

  const regenerate = async (mode) => {
    const label = mode === "all" ? "ALL curated organizations" : "organizations missing a story";
    const confirmMsg = mode === "all"
      ? `Regenerate long-form AI editorials for ALL ${aiStatus?.total_curated || "?"} curated charities?\n\nThis uses Claude Sonnet 4.5 and takes ~30 seconds per organization.`
      : `Generate long-form AI editorials for ${aiStatus?.missing || "?"} charities that don't yet have one?\n\nRuns in the background via Claude Sonnet 4.5.`;
    if (!window.confirm(confirmMsg)) return;
    setRegenerating(true);
    try {
      await http.post(`/admin/organizations/ai-stories/regenerate?mode=${mode}`);
      toast.success(`Queued — polling every 5s for progress`);
      // Safety timeout: stop the polling flag after 30 min max.
      setTimeout(() => setRegenerating(false), 30 * 60 * 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to queue");
      setRegenerating(false);
    }
  };

  const regenerateOne = async (o) => {
    if (!window.confirm(`Regenerate editorial for "${o.name}"?\n\nRuns in the background — refresh the page in ~30 seconds to see it.`)) return;
    setBusyId(o.id);
    try {
      await http.post(`/admin/organizations/${o.slug || o.id}/ai-story`);
      toast.success(`Queued — check back in ~30s`);
      // Poll status once after 45s to refresh the row.
      setTimeout(() => { loadOrgs(); loadStatus(); }, 45000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to queue");
    } finally { setBusyId(null); }
  };

  const curated = useMemo(() => orgs.filter((o) => o.is_curated), [orgs]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = curated.filter((o) => {
      if (filter === "with_story" && !o.ai_story) return false;
      if (filter === "missing_story" && o.ai_story) return false;
      if (!needle) return true;
      return (
        o.name?.toLowerCase().includes(needle) ||
        o.acronym?.toLowerCase().includes(needle) ||
        o.slug?.toLowerCase().includes(needle) ||
        o.hq_country?.toLowerCase().includes(needle)
      );
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const getKey = (o) => {
      switch (sortKey) {
        case "word_count": return o.ai_story?.word_count ?? -1;
        case "ai_story_at": return o.ai_story_at || "";
        case "country": return (o.hq_country || o.country || "").toLowerCase();
        default: return (o.name || "").toLowerCase();
      }
    };
    return [...list].sort((a, b) => {
      const av = getKey(a);
      const bv = getKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [curated, filter, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" || key === "country" ? "asc" : "desc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 opacity-40 inline ml-1" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 inline ml-1" style={{ color: "var(--wofy-primary)" }} />
      : <ArrowDown className="w-3 h-3 inline ml-1" style={{ color: "var(--wofy-primary)" }} />;
  };

  const pct = aiStatus && aiStatus.total_curated
    ? Math.round((aiStatus.with_story / aiStatus.total_curated) * 100)
    : 0;

  return (
    <div data-testid="admin-organizations">
      {/* AI editorial toolbar (mirrors AdminRecipes) */}
      <div className="mb-5 p-4 border rounded-lg flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: "var(--wofy-line)", background: "rgba(196,127,58,0.08)" }}>
        <div className="text-sm min-w-0">
          <div className="font-semibold flex items-center gap-2">
            <Sparkles size={14} style={{ color: "#c47f3a" }} /> AI editorial profiles (Claude Sonnet 4.5)
          </div>
          {aiStatus ? (
            <>
              <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
                {aiStatus.with_story} of {aiStatus.total_curated} curated charities have a long-form editorial · {aiStatus.missing} still missing
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
                <div className="h-full" style={{ width: `${pct}%`, background: "#c47f3a", transition: "width 0.4s ease" }} />
              </div>
            </>
          ) : (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>Loading status…</div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-orgs-regen-missing"
            onClick={() => regenerate("missing")}
            disabled={regenerating || (aiStatus && aiStatus.missing === 0)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#eee" : "#c47f3a", color: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#888" : "white" }}
          >
            <RefreshCw size={13} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Running…" : `Generate for missing${aiStatus ? ` (${aiStatus.missing})` : ""}`}
          </button>
          <button
            data-testid="admin-orgs-regen-all"
            onClick={() => regenerate("all")}
            disabled={regenerating}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            <Sparkles size={13} /> Regenerate ALL
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input
            data-testid="admin-orgs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, acronym, country…"
            className="w-full pl-9 pr-3 py-1.5 border rounded text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-line)" }}
          />
        </div>
        {[
          { v: "all", label: `All (${curated.length})` },
          { v: "missing_story", label: `Missing story (${aiStatus?.missing ?? "?"})` },
          { v: "with_story", label: `With story (${aiStatus?.with_story ?? "?"})` },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`admin-orgs-filter-${f.v}`}
            onClick={() => setFilter(f.v)}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded border"
            style={filter === f.v
              ? { borderColor: "var(--wofy-primary)", background: "var(--wofy-primary)", color: "white" }
              : { borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--wofy-line)" }}>
        <table className="w-full text-sm">
          <thead style={{ background: "var(--wofy-surface)" }}>
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              <th className="px-3 py-2 select-none cursor-pointer hover:opacity-80" onClick={() => toggleSort("name")} data-testid="admin-orgs-sort-name">
                Organization<SortIcon col="name" />
              </th>
              <th className="px-3 py-2 select-none cursor-pointer hover:opacity-80" onClick={() => toggleSort("country")} data-testid="admin-orgs-sort-country">
                Country<SortIcon col="country" />
              </th>
              <th className="px-3 py-2 select-none cursor-pointer hover:opacity-80" onClick={() => toggleSort("word_count")} data-testid="admin-orgs-sort-words">
                Editorial<SortIcon col="word_count" />
              </th>
              <th className="px-3 py-2 select-none cursor-pointer hover:opacity-80" onClick={() => toggleSort("ai_story_at")} data-testid="admin-orgs-sort-generated">
                Generated<SortIcon col="ai_story_at" />
              </th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} data-testid={`admin-org-row-${o.slug || o.id}`} className="border-t" style={{ borderColor: "var(--wofy-line)" }}>
                <td className="px-3 py-2">
                  <div className="font-medium">{o.name}</div>
                  {o.acronym && o.acronym !== o.name && (
                    <div className="text-[11px] font-mono opacity-60">{o.acronym}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{o.hq_country || o.country || "—"}</td>
                <td className="px-3 py-2">
                  {o.ai_story ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(34,139,34,0.12)", color: "green" }}>
                      ✓ {o.ai_story.word_count || 0} words
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(196,127,58,0.15)", color: "#c47f3a" }}>
                      Missing
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                  {o.ai_story_at ? new Date(o.ai_story_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-org-regen-${o.slug || o.id}`}
                    onClick={() => regenerateOne(o)}
                    disabled={busyId === o.id}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border mr-2"
                    style={{ borderColor: "var(--wofy-line)" }}
                    title="Regenerate this org's editorial"
                  >
                    <RefreshCw size={11} className={busyId === o.id ? "animate-spin" : ""} />
                    {busyId === o.id ? "Working…" : (o.ai_story ? "Regenerate" : "Generate")}
                  </button>
                  <Link
                    to={`/organizations/${o.slug || o.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-org-view-${o.slug || o.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border"
                    style={{ borderColor: "var(--wofy-line)" }}
                  >
                    View <ExternalLink size={11} />
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>No organizations match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
